import { useCallback, useMemo } from "react";

import { SessionInferenceMode } from "@/components/graphql/schema.graphql";
import { FeatureFlag } from "@/features/users/featureFlags";
import { Permission } from "@/types";
import { getConnectionNodesTyped } from "@/utils/relay";

import useBasicData from "./useBasicData";

interface UseAccessResult {
  isInternalUser: boolean;
  hasFeature: (feature: FeatureFlag) => boolean;
  hasPermission: (permission: Permission) => boolean;
  hasPermissionInWorkspace: (
    permission: Permission,
    workspaceId: string,
  ) => boolean;
  hasInferenceMode: (modality: SessionInferenceMode) => boolean;
}

export const useAccess = (): UseAccessResult => {
  const { user, featureFlags, workspace } = useBasicData();

  const userPermissionsMapByWorkspace = useMemo(() => {
    const userWorkspaces = getConnectionNodesTyped(user?.memberships ?? {}).map(
      (membership) => membership.workspace,
    );
    return new Map<string, Map<string, boolean>>(
      userWorkspaces.map((userWorkspace) => [
        userWorkspace.id,
        new Map(
          userWorkspace?.myPermissions.map((permission) => [permission, true]),
        ),
      ]),
    );
  }, [user?.memberships]);

  const workspaceInferenceModes = useMemo(() => {
    return workspace?.availableInferenceModes
      ? workspace?.availableInferenceModes.reduce<
          Partial<Record<SessionInferenceMode, boolean>>
        >((acc, mode) => Object.assign(acc, { [mode]: true }), {})
      : {};
  }, [workspace]);

  const hasFeature = useCallback(
    (featureFlag: FeatureFlag) => !!featureFlags?.[featureFlag],
    [featureFlags],
  );

  const hasPermissionInWorkspace = useCallback(
    (permission: Permission, workspaceId?: string) => {
      const effectiveWorkspaceId = workspaceId ?? workspace?.id;
      if (!effectiveWorkspaceId) return false;
      return Boolean(
        userPermissionsMapByWorkspace
          .get(effectiveWorkspaceId)
          ?.has(permission),
      );
    },
    [userPermissionsMapByWorkspace, workspace?.id],
  );

  const hasPermission = useCallback(
    (permission: Permission) => hasPermissionInWorkspace(permission),
    [hasPermissionInWorkspace],
  );

  const hasInferenceMode = useCallback(
    (inferenceMode: SessionInferenceMode) =>
      !!workspaceInferenceModes[inferenceMode],
    [workspaceInferenceModes],
  );

  return useMemo(
    () => ({
      isInternalUser: user?.email?.endsWith("@layer.ai") ?? false,
      hasFeature,
      hasPermission,
      hasPermissionInWorkspace,
      hasInferenceMode,
    }),
    [
      user?.email,
      hasFeature,
      hasPermission,
      hasPermissionInWorkspace,
      hasInferenceMode,
    ],
  );
};
